import React, { useEffect, useState } from "react";
import { api, apiError, BACKEND_URL } from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { Sparkles, Save, X, Tag } from "lucide-react";


export default function PreparePostDialog({
  open,
  onOpenChange,
  clientId,
  clientName,
  mediaUrl,
  mediaKind,
  sourceMessageId,
  onCreated,
}) {
  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [hashtags, setHashtags] = useState(""); // space-separated in UI
  const [note, setNote] = useState("");
  const [genLoading, setGenLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle("");
      setCaption("");
      setHashtags("");
      setNote("");
    }
  }, [open]);

  const aiGenerate = async () => {
    setGenLoading(true);
    try {
      const { data } = await api.post("/staff/posts/ai-caption", {
        client_id: clientId,
        media_kind: mediaKind || "video",
        note: note || "",
      });
      setTitle(data.title || "");
      setCaption(data.caption || "");
      setHashtags((data.hashtags || []).join(" "));
      toast.success("Post generato con AI ✨");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setGenLoading(false);
    }
  };

  const save = async () => {
    if (!caption.trim() && !hashtags.trim() && !title.trim()) {
      toast.error("Aggiungi almeno un titolo o una caption");
      return;
    }
    setSaving(true);
    try {
      const tagArr = hashtags
        .split(/\s+/)
        .map((t) => t.trim())
        .filter(Boolean)
        .map((t) => (t.startsWith("#") ? t : "#" + t))
        .slice(0, 5);
      const payload = {
        client_id: clientId,
        title: title.trim(),
        caption,
        hashtags: tagArr,
        media_url: mediaUrl || "",
        media_kind: mediaKind || "video",
        scheduled_at: null,
        source_message_id: sourceMessageId || null,
      };
      await api.post("/staff/posts", payload);
      toast.success(
        (mediaKind || "video") === "foto"
          ? "Bozza salvata in Coda Foto"
          : "Bozza salvata in Coda Video"
      );
      onCreated && onCreated();
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="prepare-post-dialog"
        className="max-w-xl max-h-[85vh] overflow-y-auto rounded-3xl"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
            <Sparkles className="h-5 w-5" /> Prepara post
          </DialogTitle>
          <DialogDescription>
            {clientName
              ? `Cliente: ${clientName}`
              : "Genera caption e hashtag con AI, poi programma in Coda Metricool."}
          </DialogDescription>
        </DialogHeader>

        {mediaUrl && (
          <div className="rounded-2xl border border-[var(--mp-border)] bg-[var(--mp-dash-bg)] p-2 flex items-center gap-3">
            {mediaKind === "video" ? (
              <video
                src={`${BACKEND_URL}${mediaUrl}`}
                className="h-24 rounded-xl"
                controls
              />
            ) : (
              <img
                src={`${BACKEND_URL}${mediaUrl}`}
                alt="anteprima"
                className="h-24 rounded-xl object-cover"
              />
            )}
            <div className="text-[11px] text-[var(--mp-muted)] min-w-0">
              Il media (dal Dropbox del cliente) verrà allegato al post quando lo pubblicherai da Coda Metricool.
            </div>
          </div>
        )}

        {/* Hint field (used by AI) */}
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] mb-1.5 block">
            Hint per l'AI (opzionale)
          </label>
          <Input
            data-testid="prep-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Es: balayage biondo caldo, oggi tutorial anti-crespo…"
            className="rounded-full border-[var(--mp-border)] h-10"
          />
        </div>

        {/* Title */}
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] mb-1.5 block">
            Titolo (visibile in copertina/anteprima)
          </label>
          <Input
            data-testid="prep-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Es: Balayage biondo caldo"
            maxLength={60}
            className="rounded-full border-[var(--mp-border)] h-10"
          />
          <div className="mt-1 text-[11px] text-[var(--mp-muted)]">
            Max 60 caratteri. Generato dall'AI insieme alla caption.
          </div>
        </div>

        {/* Caption */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
              Caption
            </label>
            <Button
              size="sm"
              onClick={aiGenerate}
              disabled={genLoading}
              data-testid="prep-ai-btn"
              className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white h-8 text-xs"
            >
              <Sparkles className="h-3.5 w-3.5 mr-1" />
              {genLoading ? "Genero…" : "Prepara con AI"}
            </Button>
          </div>
          <Textarea
            data-testid="prep-caption"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Caption del post (clicca Genera con AI per una proposta)"
            className="min-h-[140px] rounded-2xl border-[var(--mp-border)]"
          />
        </div>

        {/* Hashtags */}
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] mb-1.5 block flex items-center gap-1">
            <Tag className="h-3 w-3" /> Hashtag (separati da spazio)
          </label>
          <Textarea
            data-testid="prep-hashtags"
            value={hashtags}
            onChange={(e) => setHashtags(e.target.value)}
            placeholder="#parrucchiere #milano #taglio #balayage …"
            className="min-h-[70px] rounded-2xl border-[var(--mp-border)] font-mono text-sm"
          />
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-[var(--mp-border)]">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-full border-[var(--mp-ink)]"
            data-testid="prep-cancel"
          >
            <X className="h-4 w-4 mr-1" /> Annulla
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            data-testid="prep-save"
            className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
          >
            <Save className="h-4 w-4 mr-1" />
            {saving ? "Salvataggio…" : "Programma"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
